/**
 * Quora Pixel — self-registering.
 */

import { warn } from '../../util/log';
import type { QuoraPixel } from '../../core/config/types';
import { on, SdkEvent } from '../../core/events/bus';
import type { PixelRegister } from './types';

declare global {
  interface Window {
    qp?: (...args: unknown[]) => void;
  }
}

const SCRIPT_URL = 'https://a.quora.com/qevents.js';
let scriptInjected = false;

function ensureQp(): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { qp?: ((...args: unknown[]) => void) & { qp?: unknown } };
  if (typeof w.qp !== 'function') {
    const stub = function (...args: unknown[]): void {
      const fn = stub as unknown as { qp?: unknown[] };
      if (Array.isArray(fn.qp)) fn.qp.push(args);
      else fn.qp = [args];
    } as unknown as Window['qp'];
    w.qp = stub;
  }
  if (scriptInjected) return;
  scriptInjected = true;
  try {
    const s = document.createElement('script');
    s.async = true;
    s.src = SCRIPT_URL;
    (document.head || document.documentElement).appendChild(s);
  } catch (err) {
    warn('quora script inject failed', err);
  }
}

// NOTE: `qp('track', ...)` is global — Quora's tag API has no per-pixel
// scoping, so multiple Quora pixels on one page would multiply events.
export const registerQuora: PixelRegister<QuoraPixel> = (pixel, context) => {
  let inited = false;

  on(SdkEvent.PAGE_VIEW, () => {
    if (context.getConsent() !== 'granted') return;
    try {
      ensureQp();
      if (!inited) {
        inited = true;
        window.qp!('init', pixel.pixel_id);
      }
      window.qp!('track', 'ViewContent');
    } catch (err) {
      warn('quora pageview failed', err);
    }
  });

  on(SdkEvent.ECOM_CLICKOUT, () => {
    if (context.getConsent() !== 'granted') return;
    try {
      window.qp?.('track', 'GenerateLead');
    } catch (err) {
      warn('quora clickout failed', err);
    }
  });

  // whole cents ≤50 (yoto parity)
  on(SdkEvent.SESSION_BID_CENT_INCREMENT, (cents) => {
    if (context.getConsent() !== 'granted') return;
    if (cents <= 0 || cents > 50 || cents % 1 !== 0) return;
    try {
      window.qp?.('track', 'Generic');
      window.qp?.('track', `${cents}cents`);
    } catch (err) {
      warn('quora cent event failed', err);
    }
  });
};

export function _resetQuoraForTests(): void {
  scriptInjected = false;
}
